import * as React from "react";
import {canDisplayLead, getLeadByIdOrRefNo} from "../store/view/lead";
import {fetchLeadByRefNo, fetchLeadEvents} from "../store/action/lead/lead";
import Note from "../model/note/Note";
import Lead from "../model/lead/Lead";
import {none, option, Option} from "ts-option";
import {connect, Dispatch} from "react-redux";
import {RootState} from "../store/reducer/root/RootState";
import {withRouter} from "react-router-dom";
import {fetchNotes} from "../store/action/note/notes";
import LeadEvent from "../model/lead/event/LeadEvent";
import LeadStatusEvent from "../model/lead/event/LeadStatusEvent";
import {RouteComponentProps} from "react-router";
import Routes from "../navigation/route/Route";
import LeadStatusSelector from "../components/lead/statusSelector/LeadStatusSelector";
import Loading from "../components/ui/loading/Loading";
import LeadActivityStream from "../components/activityStream/LeadActivityStream";
import LeadActivityTabs, {LeadTab} from "../components/lead/activityTabs/LeadActivityTabs";
import Appointment from "../model/appointment/Appointment";
import {OnFailure, OnSuccess} from "../store/action/actionHelpers";
import {fetchAddressHistory} from "../store/action/addressHistory/addressHistory";
import ButtonSecondary from "../hiberUiKit/src/atoms/Buttons/ButtonSecondary";
import {getIndividualContact} from "../store/view/contact";
import IndividualContact from "../model/contact/IndividualContact";
import {isDefined, notifyBugsnagAndShowToast} from "../utils/Utils";
import {ImportantNotes} from "../components/importantNotes/ImportantNotes";
import PartnerBrand from "../model/partnerBrand/PartnerBrand";
import {getPartnerBrand} from "../store/view/partnerBrand";
import TextUtils from "../utils/TextUtils";
import Toast from "../components/helper/Toast";
import PartnerBrandAssignee from "../components/partnerBrand/PartnerBrandAssignee";
import LeadAgentSelector from "../components/lead/v4/agentSelector/LeadAgentSelector";
import MerchantBranchLocation from "../model/merchant/MerchantBranchLocation";
import LeadPinnedNotes from "../components/lead/pinnedNotes/LeadPinnedNotes";
import LeadSidebar from "../components/lead/leadSidebar/LeadSidebar";
import ActivityStreamUtils from "../utils/ActivityStreamUtils";
import {fetchLeadAppointments} from "../store/action/appointment/appointment";
import Settings from "../model/settings/Settings";
import DeepLinkLinks from "../components/deeplink/DeepLinkLinks";

interface RouteProps {
	/**
	 * Lead identifier.
	 */
	leadReferenceNumber: string
}

type ComponentProps = RouteComponentProps<RouteProps>;

interface ReduxProps {
	/**
	 * Lead data.
	 */
	lead: Option<Lead>
	/**
	 * Lead notes.
	 */
	notes: Note[]
	/**
	 * Lead histories.
	 */
	leadEvents: Array<LeadEvent | LeadStatusEvent>
	/**
	 * Lead appointments.
	 */
	appointments: Appointment[];
	/**
	 * Contact
	 */
	contact: Option<IndividualContact>;
	/**
	 * Can display lead
	 */
	canDisplayLead: boolean
	/**
	 * Partner brand
	 */
	partnerBrand: Option<PartnerBrand>
	/**
	 * Configuration
	 */
	config: Option<Settings>
}

interface DispatchProps {
	fetchLeadByRefNo: (refNo: string, onSuccess: OnSuccess, onFailure: OnFailure) => void;
	/**
	 * Fetch notes action.
	 * @param {string} leadId lead identifier.
	 */
	fetchNotes: (leadId: string) => void;
	/**
	 * Fetch lead history.
	 * @param {string} leadId lead identifier.
	 */
	fetchLeadHistory: (leadId: string) => void;
	/**
	 * Fetch leads
	 * @param {OnSuccess} onSuccess
	 * @param {OnFailure} onFailure
	 */
	fetchLeads: (onSuccess: OnSuccess, onFailure: OnFailure) => void;
	/**
	 * Fetch address history for given contact.
	 * @param {string} contactId contact id.
	 */
	fetchAddressHistory: (contactId: string) => void;
	/**
	 * Fetch lead appointments
	 * @param leadId
	 */
	fetchAppointments: (leadId: string) => void
}

interface State {
	/**
	 * Note to edit.
	 */
	noteToEdit: Option<Note>;
	/**
	 * Appointment to edit.
	 */
	appointmentToEdit: Option<Appointment>
	/**
	 * Selected tab id.
	 */
	selectedTabId: LeadTab
	/**
	 * Lead feting in progress
	 */
	leadFetching: boolean;
	/**
	 * Lead fetched
	 */
	leadFetched: boolean;
	/**
	 * Preferred merchant branch location
	 */
	preferredMerchantBranchLocation: Option<MerchantBranchLocation>
}

type Props = ComponentProps & ReduxProps & DispatchProps & RouteComponentProps<RouteProps>;

class LeadDetails extends React.PureComponent<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state =
			{
				noteToEdit: none,
				appointmentToEdit: none,
				selectedTabId: LeadTab.Note,
				leadFetched: false,
				leadFetching: false,
				preferredMerchantBranchLocation: none
			};
		this.onSuccess = this.onSuccess.bind(this);
		this.onEditNoteClick = this.onEditNoteClick.bind(this);
		this.onNoteEditSuccess = this.onNoteEditSuccess.bind(this);
		this.onEditAppointmentClick = this.onEditAppointmentClick.bind(this);
		this.onAppointmentEditSuccess = this.onAppointmentEditSuccess.bind(this);
		this.onTabsSwitch = this.onTabsSwitch.bind(this);
		this.checkLeadExists = this.checkLeadExists.bind(this);
		this.onCreateQuoteClick = this.onCreateQuoteClick.bind(this);
	}

	componentDidMount() {
		this.props.lead.map(l => {
			this.props.fetchNotes(l.id);
			this.props.fetchLeadHistory(l.id);
			this.props.fetchAddressHistory(l.data.principalContactId);
			this.props.fetchAppointments(l.id);
		});
		this.checkLeadExists()
	}

	componentWillReceiveProps(nextProps: Props) {
		if ((this.props.lead.isEmpty && nextProps.lead.isDefined) ||
			(this.props.lead.isDefined && nextProps.lead.isDefined && this.props.lead.get.id !== nextProps.lead.get.id)) {
			nextProps.lead.map(l => {
				this.props.fetchNotes(l.id);
				this.props.fetchLeadHistory(l.id);
				this.props.fetchAppointments(l.id);
			});
		}
		if (this.props.lead.isDefined && nextProps.lead.isEmpty) {
			this.setState({leadFetched: false, leadFetching: false});
		}
		this.checkLeadExists(nextProps.lead);
	}

	render() {
		const bdpId = this.props.partnerBrand.flatMap(pb => pb.bdpId);
		const accountManagerId = this.props.partnerBrand.flatMap(pb => pb.accountManagerId);
		return (
			<div className={"l-page boxed"}>
				<Loading isLoading={this.props.lead.isEmpty || !this.props.canDisplayLead}>
					{
						this.props.lead.map(lead => {
							return (
								<div className={"l-page_header"}>
									<div className="l-lead-details-header">
										<div className={"l-lead-details-header_reference-number-and-button"}>
											<div>
												<div className={"t-module-heading"}>Lead reference</div>
												<div title={"Copy lead reference"}
													 className={"t-hero l-reference-number"}
													 onClick={this.copyReferenceNumberToClipboard}>
													{lead.referenceNumber}
												</div>
											</div>
											<DeepLinkLinks onUniversalDeeplinkClick={this.copyUniversalLinkToClipboard}
														   onAppDeeplinkClick={this.copyAppLinkToClipboard}/>
										</div>
										<div className={"l-lead-details-header_activity"}>
											<div style={{display: "inline", marginRight: "20px"}}>
												<ButtonSecondary label={"Create quote"} onClick={this.onCreateQuoteClick}/>
											</div>
											<LeadAgentSelector agentId={lead.agentId} lead={lead}/>
											<PartnerBrandAssignee title="AM" userId={accountManagerId}/>
											<PartnerBrandAssignee title="BDP" userId={bdpId}/>
											<LeadStatusSelector lead={lead}/>
										</div>
									</div>
								</div>
							)
						}).orNull
					}
					<ImportantNotes partnerBrand={this.props.partnerBrand}/>
					<LeadPinnedNotes notes={this.props.notes.filter(note => note.pinningData.pinned && !note.isReminder()).sort(
						ActivityStreamUtils.sort)}
									 leadId={this.props.lead.map(l => l.id)}/>
					<div className={"l-page_content l-lead-details"}>
						<div className="l-lead-details_info">
							<LeadSidebar lead={this.props.lead} contact={this.props.contact}/>
						</div>
						<div className={"l-lead-details_activity"}>
							<LeadActivityTabs lead={this.props.lead}
											  noteToEdit={this.state.noteToEdit}
											  onNoteEditSuccess={this.onNoteEditSuccess}
											  appointmentToEdit={this.state.appointmentToEdit}
											  onAppointmentEditSuccess={this.onAppointmentEditSuccess}
											  onTabsSwitch={this.onTabsSwitch}
											  selectedTabId={this.state.selectedTabId}
											  partnerBrandId={this.props.contact.flatMap(c => c.data.partnerBrandId)}/>
							<div className={"l-lead-details_activity_stream"}>
								<LeadActivityStream notes={this.props.notes}
													leadEvents={this.props.leadEvents}
													activeNoteId={this.props.location.state ?
														this.props.location.state.activeId : ""}
													onEditNoteClick={this.onEditNoteClick}
													appointments={this.props.appointments}
													onEditAppointmentClick={this.onEditAppointmentClick}/>
							</div>
						</div>
					</div>
				</Loading>
			</div>
		);
	}

	onCreateQuoteClick() {
		this.props.lead.map(l => {
			this.props.contact.map(c => {
				this.props.history.push(Routes.CreateLeadQuote(l.referenceNumber))
			});
		});
	}

	/**
	 * If no lead try fetch leads again. If there is still no lead, redirect to the lead list
	 */
	checkLeadExists(lead = this.props.lead) {
		if (lead.isEmpty) {
			if (this.state.leadFetched) {
				return
			}
			else if (!this.state.leadFetching) {
				this.setState({leadFetching: true},
					() => this.props.fetchLeadByRefNo(
						this.props.match.params.leadReferenceNumber,
						() => {this.setState({leadFetched: true, leadFetching: false})},
						() => this.props.history.push(Routes.LeadsList))
				);
			}
		}
	}

	/**
	 * Scroll page up to the edition fields.
	 */
	scrollToEdition() {
		if (window.scrollY > 100)
			window.scrollTo(0, 100);
	}

	/**
	 * On successfully modified contact.
	 */
	private onSuccess() {
		this.props.history.push(Routes.LeadsList);
	}

	/**
	 * On edit note click action.
	 * @param {Note} note note to edit.
	 */
	private onEditNoteClick(note: Note) {
		this.setState({
			noteToEdit: option(note),
			selectedTabId: note.reminderDate.isDefined ? LeadTab.Reminder : LeadTab.Note
		});
		this.scrollToEdition();
	}

	/**
	 * On edit appointment click callback.
	 * @param {string} appointmentId
	 */
	private onEditAppointmentClick(appointmentId: string) {
		this.setState({
			appointmentToEdit: option(this.props.appointments.filter(a => a.id === appointmentId)[0]),
			selectedTabId: LeadTab.Appointment
		});
		this.scrollToEdition();
	}

	/**
	 * Clear note to edit on edit success.
	 */
	private onNoteEditSuccess() {
		this.setState({noteToEdit: none, selectedTabId: LeadTab.Note})
	}

	private onAppointmentEditSuccess() {
		this.setState({appointmentToEdit: none, selectedTabId: LeadTab.Note})
	}

	private onTabsSwitch(tabId: LeadTab) {
		this.setState({noteToEdit: none, appointmentToEdit: none, selectedTabId: tabId});
	}

	private copyUniversalLinkToClipboard = () => {
		this.props.config.map(c =>
			this.props.lead.map(l => {
				TextUtils.copyToClipboard(`${c.hiberWebsiteUrl}/deeplink/customer/${l.data.principalContactId}`);
				Toast.success("Mobile universal deep-link has been copied to clipboard", "Copy link");
			}));
	};

	private copyAppLinkToClipboard = () => {
		this.props.lead.map(l => {
			TextUtils.copyToClipboard(`[Lead details](partnerapp://customer/${l.data.principalContactId})`);
			Toast.success("Mobile app deep-link has been copied to clipboard", "Copy link");
		});
	};

	private copyReferenceNumberToClipboard = () => {
		this.props.lead.map(l => {
			TextUtils.copyToClipboard(l.referenceNumber);
			Toast.success("Lead reference has been copied to clipboard", "Copy lead reference");
		});
	}
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		fetchLeadByRefNo: (refId: string, onSuccess: OnSuccess, onFailure: OnFailure) => dispatch(
			fetchLeadByRefNo(refId, onSuccess, onFailure)),
		fetchNotes: (leadId: string) => dispatch(fetchNotes(leadId, () => {}, () => {})),
		fetchLeadHistory: (leadId: string) => dispatch(fetchLeadEvents(leadId, () => {}, () => {})),
		fetchAddressHistory: (contactId: string) => dispatch(fetchAddressHistory(contactId, () => {}, () => {})),
		fetchAppointments: (leadId: string) => dispatch(
			fetchLeadAppointments(leadId, () => {}, e => notifyBugsnagAndShowToast(e, "Fetch lead appointments")))
	}
}

function mapStateToProps(state: RootState, props: ComponentProps) {
	const cdl = canDisplayLead(state);
	const lead: Option<Lead> = cdl ? getLeadByIdOrRefNo(props.match.params.leadReferenceNumber, state) : none;
	const leadId = lead.map(l => l.id).orNull;
	const storeNotes = state.note.notes[leadId];
	const notes: Note[] = !isDefined(storeNotes) ? [] : storeNotes;
	const leadEvents = state.lead.events.getOrElse([]).filter(event => event.leadId === leadId);
	const appointments = state.appointment.appointments[leadId] || []
	const contact = lead.flatMap(l => getIndividualContact(l.data.principalContactId, state));
	const partnerBrandId = contact.flatMap(c => c.data.partnerBrandId);
	const partnerBrand = partnerBrandId.flatMap(pbId => getPartnerBrand(pbId, state));
	const config = state.settings.settings
	return ({lead, notes, leadEvents, appointments, contact, canDisplayLead: cdl, partnerBrand, config})
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(LeadDetails));